import React, {Component, Fragment} from 'react';
import {inject, observer} from 'mobx-react';
import {action, observable, reaction, runInAction} from 'mobx';
import isUndefined from 'lodash/isUndefined';
import {FormattedMessage} from 'react-intl';
import MediaSelector from '../../components/mediaSelector';
import {CreateModeContainer} from '../../containers/app/componentLoader';
import logo from '../../assets/images/logo-main.svg';
import {go} from '../../utils/navigationUtility';
import {isIpad, isIpadChrome} from '../../utils/screenUtility';
import {DeviceType, PlatformMode} from '../../constants/enums';
import {getId, getSchoolId, isGuest, isStudent, isTeacher} from '../../utils/userUtility';
import RotateContainer from '../rotate';
import ExitCreateModeActionsContainer from './ExitCreateModeActionsContainer';
import StudioMobileContainer from './studio-mobile';
import ProgressLoader from '../../components/ProgressLoader';
import './studio.scss';
import {unauthenticatedAccess} from '../../utils/awsUtility';
import {isPortrait} from "../../utils/deviceUtility";
import UploadsInProgressDialogContainer from './UploadsInProgressDialogContainer';
import AppDialogsContainer from '../dialog/AppDialogsContainer';
import IdleTimer from "react-idle-timer";

const PRACTICE_INTERVAL = 30;

@inject('looperStore', 'mainStore', 'projectStore', 'userStore', 'manageStore', 'dialogStore', 'actionStore', 'uploadsProgressStore')
@observer
class CreateContainer extends Component {
    @observable isLoadStudioMode = true;
    @observable isBeforeMinLoadTime = true;
    @observable portrait = isPortrait();
    @observable isAuthenticated = false;
    @observable isStudentsFetching = true;
    @observable unsavedChangesTextObject = {
        question: 'studio.saveLessonDialog.header.unsavedChanges',
        message: 'studio.saveLessonDialog.message.unsavedChanges'
    };
    @observable skipUploadsInProgress = false;

    @action setOrientation = () => {
        this.portrait = isPortrait();
    };

    constructor(props) {
        super(props);
        this.idleTimer = null;
        this.isIdle = false;
    }

    async componentWillMount() {
        window.addEventListener('resize', this.setOrientation, false);
        const {mainStore, match, projectStore, userStore, manageStore} = this.props;
        const {projectId} = match.params;
        if (projectId) {
            try {
                await userStore.authenticate();
                manageStore.fetchTeachersList();
                if (isTeacher()) {
                    manageStore.fetchStudentList();
                }
                runInAction(() => {
                    this.isAuthenticated = true;
                })
            } catch (err) {
                await unauthenticatedAccess();
            }
            try {
                mainStore.updatePlatformMode(mainStore.platformMode !== PlatformMode.Home ? mainStore.platformMode : PlatformMode.Edit);
                this.init();

                const project = await projectStore.loadLesson(projectId, isStudent());
                const isUserProjectOwner = project.owner.id === getId();
                if (!mainStore.isHomeMode) { //handle cornercase where browser back was pressed during studio load
                    mainStore.updatePlatformMode(isUserProjectOwner ? PlatformMode.Edit : PlatformMode.View);
                }
            } catch (err) {
                console.log('error loading project: projectId: ', projectId, ' error: ', err);
                go('/');
            } finally {
                runInAction(() => {
                    this.isLoadStudioMode = false;
                    this.setPracticeTimelisteners();
                });
            }
        } else if (mainStore.platformMode === PlatformMode.Home) {
            go('/');
        } else {
            this.init();
            runInAction(() => {
                this.isLoadStudioMode = false;
                this.setPracticeTimelisteners();
            });
        }
    }

    setPracticeTimelisteners = () => {
        this.idListener = reaction(
            () => this.props.looperStore.loopedProject.id,
            (id) => {
                if (id && isStudent()) {
                    this.projectId = id;
                    this.updatePracticeTime();
                    this.timer = setInterval(() => {
                        this.updatePracticeTime();
                    }, (1000 * PRACTICE_INTERVAL));
                }
            }, {fireImmediately:true}
        );
    }

    updatePracticeTime = () => {
        const {projectStore, looperStore} = this.props;
        if ((!this.isIdle || looperStore.isPlayingTracks) && looperStore.loopedProject.id) {
            projectStore.updatePracticeTime(looperStore.loopedProject.id, PRACTICE_INTERVAL);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.setOrientation);
        if (this.timer) {
            clearInterval(this.timer);
        }
        if (this.idListener) {
            this.idListener();
        }
        if (this.projectId && isStudent()) {
            this.props.projectStore.fetchProject(this.projectId, true); //to update new practice time in project store
        }
    }

    @action init = () => {
        const {looperStore} = this.props;
        const {setOrderBy, stopAll} = looperStore;
        setTimeout(() => {
            this.onMinLoadTimePassed();
        }, 1500);
        setOrderBy('duration');
        stopAll(true);

        // mainStore.finishStemsAnimation(!looperTracks.length);
    };

    @action onMinLoadTimePassed = () => {
        this.isBeforeMinLoadTime = false;
    };

    @action onExitCreateMode = () => {
        const {looperStore, dialogStore, uploadsProgressStore} = this.props;
        const isUserHasPermisssion = getId() === looperStore.loopedProject.userId || getId() === looperStore.loopedProject.parentOwnerId;
        if (uploadsProgressStore.hasActiveUploads() && !this.skipUploadsInProgress) {
            dialogStore.openUploadsInProgressConfirm();
            return;
        }
        if ((looperStore.isEditedProject && isUserHasPermisssion) || isUndefined(looperStore.loopedProject.id)) {
            this.setUnsavedChangesTextObject();
            dialogStore.openSaveLessonConfirm(false);
        } else {
            go('/');
        }
    };

    @action onCancelUploadsInProgress = () => {
        this.skipUploadsInProgress = true;
        this.props.dialogStore.closeUploadsInProgressConfirm();
        this.props.uploadsProgressStore.resetUploadsList();
        this.props.mainStore.hideProgressLoader();
        this.onExitCreateMode();
    };

    setUnsavedChangesTextObject = () => {
        const {loopedProject} = this.props.looperStore;
        let type = 'unsavedChanges';
        if (!loopedProject.id) {
            if (loopedProject.userId && (loopedProject.userId !== getId() || loopedProject.groupId)) {
                type = `assignments.${loopedProject.userId !== getId() ? 'student' : 'group'}`;
            } else {
                type = isTeacher() ? 'library' : 'jams';
            }
        }
        this.unsavedChangesTextObject = {question: `studio.saveLessonDialog.header.${type}`, message: `studio.saveLessonDialog.message.${type}`};
    };

    onSaveLessonChanges = async () => {
        const {projectStore, dialogStore} = this.props;
        await projectStore.handleProjectSave();
        dialogStore.closeSaveLessonConfirm();
        go('/');
    };

    onDiscardChanges = () => {
        this.props.dialogStore.closeSaveLessonConfirm();
        go('/');
    };

    onActive = (e) => {
        this.isIdle = false;
    }

    onIdle = (e) => {
        this.isIdle = true;
    }

    render() {
        const {projectStore, mainStore, looperStore, actionStore} = this.props;
        const {loopedProject, isEditedProject} = looperStore;
        const {isProcessing} = projectStore;
        const userHasPermissions = getId() === loopedProject.userId
            || getId() === loopedProject.parentOwnerId
            || (!loopedProject.userId && !loopedProject.parentOwnerId)
            || (loopedProject.schoolId === getSchoolId());
        const isDisabledSave = isProcessing || (loopedProject.id && !isEditedProject && !actionStore.isDirty) || !userHasPermissions;
        const isReadOnly = isGuest() || !userHasPermissions;


        return (
            <Fragment>
                {(this.isBeforeMinLoadTime || this.isLoadStudioMode) &&
                <div className="studio-loader">
                    <div className="studio-loader-area">
                        <img src={logo} alt="Musico"/>
                        <FormattedMessage id="create.loading"/>
                    </div>
                    {isIpadChrome() && <div className="studio-loader-message"><FormattedMessage id="create.ipad.chrome.message"/></div>}
                </div>
                }
                <MediaSelector devices={[DeviceType.Desktop, DeviceType.Tablet]}>
                    {!this.isBeforeMinLoadTime &&
                    <CreateModeContainer
                        onExitCreateMode={this.onExitCreateMode}
                        isReadOnly={isReadOnly}
                        isDisabledSave={isDisabledSave}
                        {...this.props}
                    />
                    }
                    {isIpad() && this.portrait && <RotateContainer/>}
                </MediaSelector>
                <MediaSelector devices={[DeviceType.Mobile]}>
                    {!(this.isBeforeMinLoadTime || this.isLoadStudioMode) && <StudioMobileContainer
                        onExitCreateMode={this.onExitCreateMode}
                        isReadOnly={isReadOnly}
                        isDisabledSave={isDisabledSave}
                    />
                    }
                </MediaSelector>
                <ProgressLoader className="studio-progress-loader" show={mainStore.isLoading}/>
                <ExitCreateModeActionsContainer
                    textObject={this.unsavedChangesTextObject}
                    onSave={this.onSaveLessonChanges}
                    buttonDisabled={projectStore.isProcessing}
                    onDiscard={this.onDiscardChanges}
                />
                <UploadsInProgressDialogContainer
                    onDismiss={this.onCancelUploadsInProgress}
                />
                <AppDialogsContainer/>
                {!this.isBeforeMinLoadTime && isStudent() &&
                <IdleTimer
                    ref={ref => { this.idleTimer = ref }}
                    onActive={this.onActive}
                    onIdle={this.onIdle}
                    debounce={250}
                    timeout={1000 * PRACTICE_INTERVAL}
                />
                }
            </Fragment>
        );
    }
}

export default CreateContainer;
